import { ThemeProvider } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ThemeRegistry } from "src/themes/ThemeRegistry";

import { parseCookies, setCookie } from "nookies";
import React, {
  createContext,
  PropsWithChildren,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { GetCouponDto } from "src/services/generated-api";
import { staleBlobUrls } from "src/components/photo-upload/utils/thumbnail";
import { customTheme, customThemeDark } from "src/themes";
import { getCurrencyBasedOnDomain } from "src/utils/domain.util";
import { getDomain } from "src/utils/domain.util";
import nookies from "nookies";
import {
  reportCartDropped,
  reportSuspiciousOrderCreation,
  type OrderCreationReason,
} from "src/utils/order-loss-diagnostics";
import * as Sentry from "@sentry/nextjs";
import { setGuestTokenCookies, orderApi } from "src/services/api";
import {
  shouldReconcileRestoredOrder,
  rescuedGroupIds,
  actualPhotoCount,
  type RestoredOrder,
} from "@components/order/new/utils/restore-reconcile";
import { clearOrderData, saveOrderData } from "src/utils/local-storage.util";
import {
  FINALIZED_ORDER_STATUSES,
  ORDER_SESSION_COOKIE_MAX_AGE_SECONDS,
  cartDropReason,
  orderAgeDays,
  type CartDropReason,
} from "src/utils/order-freshness";
import {
  clearOrderSessionFromUrl,
  isOrderViewPath,
} from "src/utils/order-session-url";
import {
  readOrderSessionStores,
  resolveOrderSession,
  summariseSessionStores,
} from "src/utils/order-session";
import { useCreateOrder } from "src/hooks/useOrderMutations";
import { useLocale } from "next-intl";
import { useOrderQuery } from "src/hooks/useOrder";
import { trackLoss } from "src/utils/funnel";
import { singleFlight } from "src/utils/single-flight";

export { CartDrawerProvider, useCartDrawer } from "./cart-drawer-context";

type ColorModeContextType = {
  mode: string;
  setMode: () => void;
};

export const ColorModeContext = createContext<ColorModeContextType>(
  {} as ColorModeContextType
);

export const ColorModeContextProvider: React.FC<
  PropsWithChildren<{ initialMode?: string }>
> = ({ children, initialMode }) => {
  // Initialise from the SSR-supplied cookie value so the server and
  // first client render agree on the mode. Without this the server
  // renders light, CssBaseline emits a light body{} rule, and when
  // the client switches to dark BOTH rules end up in the DOM with
  // the light one winning by source order.
  const [isMounted, setIsMounted] = useState(false);
  const [mode, setMode] = useState(initialMode || "light");

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const systemTheme = useMediaQuery(`(prefers-color-scheme: dark)`);

  useEffect(() => {
    if (isMounted && !initialMode) {
      // Only auto-detect when the server didn't supply a value.
      setMode(parseCookies()["theme"] || (systemTheme ? "dark" : "light"));
    }
  }, [isMounted, systemTheme, initialMode]);

  const toggleTheme = () => {
    const nextTheme = mode === "light" ? "dark" : "light";
    setMode(nextTheme);
    setCookie(null, "theme", nextTheme);
  };

  return (
    <ColorModeContext.Provider
      value={{
        setMode: toggleTheme,
        mode,
      }}
    >
      <ThemeRegistry>
        <ThemeProvider
          // you can change the theme colors here. example: mode === "light" ? RefineThemes.Magenta : RefineThemes.MagentaDark
          theme={mode === "light" ? customTheme : customThemeDark}
        >
          {children}
        </ThemeProvider>
      </ThemeRegistry>
    </ColorModeContext.Provider>
  );
};

export interface FileWithPreview {
  file: File;
  preview: string;
  thumbnail?: string;
  count?: number;
  isHeic?: boolean; // Track if the file is HEIC/HEIF
  convertedPreview?: string; // Store converted JPEG preview for HEIC/HEIF files
  // In-memory copy of the file's bytes, taken at add-time so the upload survives
  // an in-app WebView reclaiming the picked handle. Upload from this when set;
  // fall back to `file` otherwise. See utils/file-snapshot.ts.
  bytes?: File;
  // Identifies this pick in the reload-survival store (IndexedDB), so a pick
  // removed or uploaded can be dropped from it. See
  // components/order/new/utils/pending-picks.ts.
  pickId?: string;
}

interface FilesContextType {
  files: FileWithPreview[];
  setFiles: React.Dispatch<React.SetStateAction<FileWithPreview[]>>;
  clearFiles: () => void;
}

const FilesContext = createContext<FilesContextType | undefined>(undefined);

interface FilesProviderProps {
  children: ReactNode;
}

export const FilesProvider: React.FC<FilesProviderProps> = ({ children }) => {
  const [files, setFiles] = useState<FileWithPreview[]>([]);

  // Thumbnails are blob: URLs, which hold their bytes until revoked -- unlike
  // the data-URL strings they replaced. Release each one as soon as its photo
  // leaves the selection (removed, dropped as unreadable, uploaded, cleared).
  // Here rather than in the order page: the selection outlives that page when
  // the customer navigates away and back, and revoking on its unmount would
  // break the thumbnails they come back to.
  const shownThumbnailsRef = useRef<(string | undefined)[]>([]);
  useEffect(() => {
    const now = files.map((f) => f.thumbnail);
    staleBlobUrls(shownThumbnailsRef.current, now).forEach((url) =>
      URL.revokeObjectURL(url),
    );
    shownThumbnailsRef.current = now;
  }, [files]);

  const clearFiles = () => {
    // Records how many photos a clear destroyed and from where. Four header
    // navigation controls used to call this and wipe a customer's whole
    // selection on the way to the photo page; nothing recorded it, so three
    // customer reports in two days could not be confirmed against any data.
    // If a clear ever fires with a non-empty selection again, this names it.
    if (files.length > 0) {
      trackLoss("selection-cleared", {
        count: files.length,
        path: typeof window !== "undefined" ? window.location.pathname : "",
      });
    }
    setFiles([]);
  };

  return (
    <FilesContext.Provider value={{ files, setFiles, clearFiles }}>
      {children}
    </FilesContext.Provider>
  );
};

export const useFiles = (): FilesContextType => {
  const context = useContext(FilesContext);
  if (context === undefined) {
    throw new Error("useFiles must be used within a FilesProvider");
  }
  return context;
};
interface OrderContextType {
  // Order management state
  mainOrderId: string | null;
  guestToken: string | null;
  // True when the active order is finalized (paid / COD placed / shipped / ...).
  // The cart badge + drawer use this to render empty, so a completed order shown
  // on its own page (e.g. /summary) never behaves as the shopping cart — without
  // needing to drop mainOrderId (which the order-view pages rely on).
  activeOrderFinalized: boolean;
  /**
   * True when THIS page load restored an order whose cached photo count
   * contradicted its rows — i.e. the previous upload was interrupted and the
   * photos it managed to save were reconciled back into view.
   *
   * The upload page uses it to tell the customer what happened. Without it the
   * rescue is silent: their cart simply contains more photos than they
   * remember adding, with no explanation and no way to tell which are which.
   */
  restoreWasReconciled: boolean;
  /**
   * Ids of the groups an interrupted upload left behind, captured at DETECTION
   * time.
   *
   * It has to be a snapshot. The reconcile repairs the very counts that
   * identify these groups, so recomputing later from live order data is a race
   * against the recalculation -- the notice would appear or not depending on
   * which request landed first, and would vanish mid-session on any refetch.
   */
  rescuedGroupIdsSnapshot: string[];
  dismissRestoreReconciled: () => void;
  setOrderInfo: (orderId: string | null, token: string | null) => void;
  // Function to create an order when needed
  ensureOrderExists: (options?: {
    photoSizeId?: string;
    forceNew?: boolean;
  }) => Promise<{ orderId: string; guestToken: string }>;
}

const OrderContext = createContext<OrderContextType | undefined>(undefined);

interface OrderProviderProps {
  children: ReactNode;
}

export const OrderProvider: React.FC<OrderProviderProps> = ({ children }) => {
  const [mainOrderId, setMainOrderId] = useState<string | null>(null);
  const [guestToken, setGuestToken] = useState<string | null>(null);
  const createOrderMutation = useCreateOrder();
  const locale = useLocale();
  // ensureOrderExists' order creation, shared while one is in flight. Two
  // overlapping add-to-carts with no cart yet each saw no session and each
  // created an order; the later one became the cart and the first product was
  // stranded in an order the customer never checked out. See single-flight.ts.
  const [createOrderOnce] = useState(() =>
    singleFlight<{ orderId: string; guestToken: string }>(),
  );

  // Track the active order's status so a finalized (paid/placed) order stops
  // behaving as the cart. React Query dedupes with the cart drawer's own query.
  const { data: activeOrder } = useOrderQuery(mainOrderId, guestToken);
  const activeOrderFinalizedRef = useRef(false);
  const [activeOrderFinalized, setActiveOrderFinalized] = useState(false);
  const [restoreWasReconciled, setRestoreWasReconciled] = useState(false);
  const [rescuedGroupIdsSnapshot, setRescuedGroupIdsSnapshot] = useState<
    string[]
  >([]);

  // Order management functions
  const setOrderInfo = (orderId: string | null, token: string | null) => {
    setMainOrderId(orderId);
    setGuestToken(token);
    // Persist to EVERY store so the active order survives full page loads and is
    // used by BOTH the photo-upload flow and the card/calendar flow — one cart,
    // not two. Clearing (null) also runs on payment success (summary.tsx), so a
    // paid order is never re-adopted.
    //
    // Cookies AND localStorage, written and cleared together, deliberately. The
    // provider now RESTORES from localStorage too (see the effect below), which
    // makes a store left behind on a clear a store that resurrects the dead
    // session on the next load. Symmetry here is what makes "cleared" mean
    // cleared. Cookie lifetime is derived from CART_MAX_AGE_DAYS — it was a
    // hard-coded 47h, which expired three days before the cart was allowed to.
    if (orderId && token) {
      nookies.set(null, "orderId", orderId, {
        maxAge: ORDER_SESSION_COOKIE_MAX_AGE_SECONDS,
        path: "/",
      });
      setGuestTokenCookies(token);
      // Backup for the cookie eviction that mobile Safari / in-app webviews do
      // routinely: same pair, same lifetime, a store the browser treats
      // differently.
      saveOrderData(orderId, token);
    } else {
      nookies.destroy(null, "orderId", { path: "/" });
      setGuestTokenCookies("");
      clearOrderData();
    }
  };

  // Helper function to get URL parameters
  function getUrlParameter(name: string) {
    if (typeof window === "undefined") return "";

    name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
    const regex = new RegExp("[\\?&]" + name + "=([^&#]*)");
    const results = regex.exec(window.location.search);
    return results === null
      ? ""
      : decodeURIComponent(results[1].replace(/\+/g, " "));
  }

  // Restore the active order on mount (e.g. after a refresh). The design /
  // calendar / checkout flows persist orderId + guest_session_token into the URL
  // (via history.replaceState), into the shared cookies, and into localStorage.
  // Without this, a refresh drops the in-memory order id and the cart
  // drawer/badge render empty even though the order still exists on the server.
  useEffect(() => {
    if (mainOrderId && guestToken) return;
    // ALL FOUR stores, in their trusted order (context > url > cookie >
    // storage), through the one resolver.
    //
    // This used to read the URL and the cookie only — a two-term `||` chain per
    // half. Two separate faults lived in that:
    //   * localStorage was invisible to the provider. The provider owns
    //     mainOrderId/guestToken, which is exactly what ensureOrderExists checks
    //     before creating an order, so when a mobile browser evicted the cookie
    //     the cart rendered empty and add-to-cart minted a DUPLICATE order on
    //     top of a live one whose backup was sitting unread in localStorage.
    //   * resolving each half independently let an id from the URL pair up with
    //     a token from the cookie. resolveOrderSession refuses to mix halves —
    //     read its header for the 401s that mixing produced.
    const restored = resolveOrderSession(
      readOrderSessionStores({ orderId: mainOrderId, guestToken }),
    );
    // No COMPLETE pair anywhere is a real answer: there is no session to resume.
    // It is emphatically not a reason to clear anything — half a session left in
    // one store may still be the other half's cart, and a fresh order is only
    // ever created by an explicit customer action (ensureOrderExists).
    if (!restored) return;
    const restoredOrderId = restored.orderId;
    const restoredToken = restored.guestToken;

    // Adopt the restored order immediately so the cart is not empty while we
    // check its age; a fresh order must never flicker to empty on load.
    setOrderInfo(restoredOrderId, restoredToken);

    // Whether the restored session may still act as the cart is decided HERE,
    // once, from the server's own answer -- the only safe place to do it. The
    // customer has just landed and has no work in flight, so dropping a dead
    // cart now costs them nothing (photos are retained well beyond the age
    // ceiling; at worst they re-add). This replaces the old behaviour where a
    // stale cart was detected mid-upload / at checkout from whichever client
    // store happened to be readable, which wiped LIVE carts when a cookie was
    // transiently evicted on mobile. See order-freshness.ts for exactly which
    // answers count -- a finalized status or a known-excessive age, nothing
    // else.
    //
    // On order-VIEW routes we adopt but never drop: those pages own the order
    // they display, including a paid one on /summary, and take their guest token
    // from the query string. See isOrderViewPath in utils/order-session-url.ts
    // and the OrderInitializer note on the finalized effect below.
    const onOrderViewPath =
      typeof window !== "undefined" &&
      isOrderViewPath(window.location.pathname);

    const dropRestoredSession = (
      reason: CartDropReason,
      details: { status?: string | null; ageDays?: number | null; httpStatus?: number } = {},
    ) => {
      // Report BEFORE clearing, while the reason and the order are still in
      // hand. This is the moment a customer's cart stops existing, and until now
      // it left no trace anywhere: the drop is a success path, so no exception
      // was ever raised and Sentry had nothing. Purely observational — it reads
      // no state the clear below depends on and cannot change what happens next.
      reportCartDropped({
        reason,
        orderId: restoredOrderId,
        orderStatus: details.status ?? null,
        ageDays: details.ageDays ?? null,
        restoredFrom: restored.source,
        origin: "provider-restore",
        httpStatus: details.httpStatus,
      });
      // Unchanged: a finalized order — and only a finalized one — flips the ref.
      // `reason === "finalized"` is true in exactly the cases the old
      // `finalized` flag was, because cartDropReason() tests the same
      // FINALIZED_ORDER_STATUSES membership first and shouldDropRestoredCart now
      // delegates to it.
      const finalized = reason === "finalized";
      // Ordered so nothing can re-adopt the dead session. Flipping the ref first
      // narrows (does not close) the window in which an add-to-cart firing
      // between mount and this answer would still append to the old order: from
      // "until the cart drawer's order query resolves" down to "until this
      // fetch resolves". Closing it fully would mean awaiting a network call
      // inside add-to-cart, which is latency the customer feels and one more
      // way a hiccup can strand a live cart.
      if (finalized) activeOrderFinalizedRef.current = true;
      setOrderInfo(null, null);
      clearOrderData();
      // Cookies + React state alone are not enough: the restore above reads the
      // URL FIRST, so leaving the pair in the query string means the very next
      // reload re-adopts the dead order and this clear achieves nothing.
      clearOrderSessionFromUrl();
    };

    let cancelled = false;
    (async () => {
      try {
        const res = await orderApi.getOneBaseOrderControllerOrder(
          restoredOrderId,
          restoredToken,
        );
        const data = res.data as unknown as RestoredOrder & {
          createdAt?: string;
          status?: string;
        };
        if (cancelled || onOrderViewPath) return;
        // Finalized (paid / COD placed / shipped / ...) or too old to resume ->
        // clear every store so a fresh order is provisioned on the next action.
        // A finalized order is a completed purchase, not a cart: resuming it is
        // how a customer's second order got appended to their first, paid one.
        // Only on a KNOWN status / KNOWN age; anything unreadable keeps it.
        const nowMs = Date.now();
        // Same decision as before, but it now NAMES its branch instead of
        // collapsing to a boolean, so the report can say whether the cart went
        // for being finished or for being old. shouldDropRestoredCart is exactly
        // `cartDropReason(...) !== null`.
        const reason = cartDropReason({
          status: data.status,
          createdAt: data.createdAt,
          nowMs,
        });
        if (reason) {
          dropRestoredSession(reason, {
            status: data.status,
            ageDays: orderAgeDays(data.createdAt, nowMs),
          });
          return;
        }

        // The cart must never show a number that contradicts the order.
        //
        // A cached `totalNOfPhotos` of 0 on an order that HAS photo rows is not
        // staleness -- the order is live and perfectly resumable -- so none of
        // the drop rules above catch it, and nothing else recalculates on a
        // plain page load (GET is deliberately read-only; the recalculating
        // endpoints fire on cart INTERACTION). The customer opens their order
        // and is told they have nothing.
        //
        // Chunked upload registration makes this reachable: photos are
        // registered as they land, but the post-upload calculate-amount only
        // runs if the page survives to make it. A tab killed mid-upload leaves
        // exactly this shape -- rows present, cached total 0. Reconciling here
        // is what turns those rescued photos back into a visible cart.
        //
        // The backend gates this endpoint to PENDING/PROCESSING, so a finalized
        // order cannot have its historical total rewritten by it.
        if (shouldReconcileRestoredOrder(data)) {
          // Report the rescue, not just the failures.
          //
          // Every other signal on this path fires when something goes WRONG, so
          // a working feature is invisible and an ineffective one looks
          // identical. This is the one event that says a customer came back to
          // an upload that had been killed and got their photos returned —
          // which is the entire point, and the only way to know the rate in
          // production rather than from a controlled test.
          //
          // `info`, matching reportCartDropped: this is the system behaving
          // correctly, and putting it at warning would bury real regressions in
          // the noise. Read it by tag, not by staring at the error feed.
          const rescued = (data.orderPhotos ?? []).reduce(
            (sum, group) => sum + actualPhotoCount(group),
            0,
          );
          Sentry.captureMessage("Restored order reconciled: cart said zero", {
            level: "info",
            tags: { area: "photo-upload", stage: "restore-reconcile" },
            extra: {
              orderId: restoredOrderId,
              photosRescued: rescued,
              ageDays: orderAgeDays(data.createdAt, nowMs),
              restoredFrom: restored.source,
            },
          });
          // Flag it for the UI BEFORE awaiting: the notice must appear even if
          // the recalculation itself fails, because the customer's cart is
          // inconsistent either way and silence is the thing being fixed.
          // Snapshot BEFORE the recalculation: it rewrites orderPhoto counts
          // from the loaded photos, which is exactly the difference that
          // identifies a rescued group. Capture it while the evidence exists.
          setRescuedGroupIdsSnapshot(rescuedGroupIds(data));
          setRestoreWasReconciled(true);
          try {
            await orderApi.orderControllerCalculateAmount(
              restoredOrderId,
              restoredToken,
            );
          } catch (recalcErr) {
            // Never let reconciliation break the restore: a failed recalc
            // leaves the customer exactly where they were, which is the
            // pre-existing behaviour, not a new failure.
            console.warn("Could not reconcile restored order total", recalcErr);
          }
        }
      } catch (err) {
        // Only a definitive "gone" clears the cart. A transient network/5xx
        // error must NOT: that was exactly the false positive that stranded
        // customers. Keep the order and let a later real action re-validate.
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        if (
          !cancelled &&
          !onOrderViewPath &&
          (status === 404 || status === 401 || status === 403)
        ) {
          // Gone / not ours, not finalized -- leave the ref alone so the next
          // creation is reported as the plain "no-session" it is.
          dropRestoredSession("definitive-404-401-403", { httpStatus: status });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // Mount-only: URL / cookie are the source of truth right after a reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the active order becomes finalized (payment done / COD placed / shipped
  // / ...), drop it as the active cart: clear the cookie + state so the drawer
  // and badge render empty, and so the next add-to-cart creates a fresh order
  // instead of mutating a completed one. Single place that covers every
  // completion path (Stripe success, COD confirm, admin transitions, ...).
  useEffect(() => {
    const status = (activeOrder as { status?: string } | undefined)?.status;
    const finalized = !!status && FINALIZED_ORDER_STATUSES.has(status);
    activeOrderFinalizedRef.current = finalized;
    setActiveOrderFinalized(finalized);
    if (finalized && mainOrderId) {
      // Clear the SHARED cookies so a full page load elsewhere doesn't re-adopt
      // this finalized order as the cart. Deliberately do NOT null mainOrderId:
      // order-view pages (e.g. /order/[id]/summary via OrderInitializer) set it
      // to display the order, and nulling it here fought that initializer in an
      // infinite set→clear→set loop that hammered /user/me + download-url until
      // the API rate-limited (429) and the page flickered. Cart emptiness for a
      // finalized order is handled by `activeOrderFinalized` (badge + drawer),
      // and ensureOrderExists still starts a fresh order on add-to-cart.
      nookies.destroy(null, "orderId", { path: "/" });
      setGuestTokenCookies("");
      // ...and the localStorage backup, which the restore effect above now reads
      // as a fourth source. Clearing only the cookies would leave the finalized
      // order in localStorage, and the very next load would restore the paid
      // order as the cart — the same re-adoption the URL clear below prevents,
      // through a different door. (This is the one store that must be dropped
      // even on an order-view route: /summary displays the order from its own
      // route params and never needs the cart backup, and summary.tsx clears it
      // on payment success already.)
      clearOrderData();
      // ...and out of the URL, for the same reason. Destroying only the cookies
      // left orderId + guest_session_token in the query string, and the restore
      // effect above reads the URL FIRST -- so a reload re-adopted the finalized
      // order as the cart and the cookie clear did nothing. Never on an
      // order-view route: those pages read the guest token straight out of the
      // query string (OrderInitializer), and /summary -- where the customer
      // lands after paying -- is a finalized order by definition. Removing it
      // there would 401 the receipt and restart the set→clear→set loop above.
      if (
        typeof window !== "undefined" &&
        !isOrderViewPath(window.location.pathname)
      ) {
        clearOrderSessionFromUrl();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrder, mainOrderId]);

  // Function to ensure an order exists, creating one if necessary
  const ensureOrderExists = async (
    options: { photoSizeId?: string; forceNew?: boolean } = {}
  ): Promise<{ orderId: string; guestToken: string }> => {
    // Recovery path: the caller has determined the current order/token is stale,
    // expired, or gone (e.g. checkout validation returned 401/403/404, or the
    // stored order is finalized). Drop it entirely — React state + shared cookies
    // — and fall through to create a brand-new order so the customer is never
    // wedged on a dead cart.
    // Why we ended up creating rather than reusing. Reported below: this is
    // the only code path that can produce an EMPTY order, and production
    // sequences show an empty order appearing between a customer's failed
    // attempt and their successful one — it takes over as the active cart, the
    // next upload correctly discards it for having no photos, and the original
    // order (with their photos) is stranded.
    let creationReason: OrderCreationReason = "no-session";

    if (options.forceNew) {
      creationReason = "force-new";
      setOrderInfo(null, null);
    } else if (activeOrderFinalizedRef.current) {
      // If the active order is finalized (paid / COD placed / shipped / ...), never
      // add to it — clear it and fall through to create a fresh order below.
      creationReason = "active-order-finalized";
      setOrderInfo(null, null);
    } else {
      // If order already exists, return it
      if (mainOrderId && guestToken) {
        return { orderId: mainOrderId, guestToken };
      }

      // Adopt an order already persisted in ANY store (e.g. one the photo-upload
      // flow created) instead of creating a separate second order — this is what
      // keeps the card/calendar cart and the photo cart in sync.
      //
      // This is the last guard standing between an add-to-cart and a duplicate
      // order, and it used to consult the shared cookie alone. The provider's
      // restore has already run by now, so on the mobile-eviction path both were
      // blind to the same live session in localStorage: restore found nothing,
      // this found nothing, and a second order was created on top of the
      // customer's cart. Same resolver, same four sources, same never-mix-halves
      // rule as the restore above.
      const persisted = resolveOrderSession(
        readOrderSessionStores({ orderId: mainOrderId, guestToken }),
      );
      if (persisted) {
        setOrderInfo(persisted.orderId, persisted.guestToken);
        return {
          orderId: persisted.orderId,
          guestToken: persisted.guestToken,
        };
      }
    }

    return createOrderOnce(() =>
      createOrder(creationReason, options.photoSizeId),
    );
  };

  // The creation half of ensureOrderExists. Only ever run through
  // createOrderOnce: a call that arrives while it is in flight joins it rather
  // than reporting and creating a second order.
  const createOrder = async (
    creationReason: OrderCreationReason,
    photoSizeId: string | undefined,
  ): Promise<{ orderId: string; guestToken: string }> => {
    {
      // Snapshot BEFORE creating. If the order we are about to replace still
      // holds photos, this call is the moment the customer's work is lost.
      const cookiesNow = parseCookies();
      // Read id, status and count off the SAME object, so the report can never
      // attribute one order's photo count to another. Which order the count
      // came from, and whether it was still live, is what separates real loss
      // from a customer simply starting a second order after finishing one.
      const displaced = activeOrder as
        | { id?: string; status?: string; totalNOfPhotos?: number }
        | undefined;
      reportSuspiciousOrderCreation({
        origin: "ensureOrderExists",
        reason: creationReason,
        hadPriorOrderId: Boolean(mainOrderId),
        cookieHadOrderId: Boolean(cookiesNow.orderId),
        cookieHadGuestToken: Boolean(cookiesNow.guest_token),
        displacedPhotoCount: displaced?.totalNOfPhotos ?? 0,
        displacedOrderId: displaced?.id,
        displacedOrderStatus: displaced?.status,
        // Re-read here rather than reusing `persisted` above, because the
        // forceNew / finalized branches skip that lookup entirely and this
        // report has to describe all three. It is the same pure read either way.
        //
        // The cookie booleans above see one store, and it is the store mobile
        // browsers evict — so "a live pair was sitting in localStorage and
        // nothing looked" read identically to "first-time visitor". That is the
        // orphaned case the owner asked to be able to debug.
        stores: summariseSessionStores(
          readOrderSessionStores({ orderId: mainOrderId, guestToken }),
        ),
      });
    }

    try {
      // Get tracking parameters
      const fbc = getUrlParameter("fbclid");
      const fbp = nookies.get(null)._fbp;
      const domainName = getDomain();
      const currency = getCurrencyBasedOnDomain();

      const order = await createOrderMutation.mutateAsync({
        photoSizeId: photoSizeId ?? "",
        userId: "",
        fbc: fbc || "",
        fbp: fbp || "",
        currency: currency ?? "EUR",
        downlink: null,
        domain: domainName ?? "",
        // Real active locale (sl/hr/sk/cs/pl/de-AT/en/it). The backend
        // accepts these directly; the old code only ever resolved sl vs en,
        // so every non-Slovenian customer was tagged as English.
        languagePreference: locale,
      } as unknown as Parameters<typeof createOrderMutation.mutateAsync>[0]);

      const orderId = order.id;
      const orderToken = (order as unknown as { guestToken: string }).guestToken;

      // Sets state AND the shared cookies (orderId + guest_token).
      setOrderInfo(orderId, orderToken);

      // Update URL with order info for persistence without reloading the page.
      // Preserve the existing history.state so Next's App Router internals are
      // not wiped (passing null there can break client-side navigation).
      if (typeof window !== "undefined") {
        const params = new URLSearchParams(window.location.search);
        params.set("orderId", orderId);
        params.set("guest_session_token", orderToken);
        window.history.replaceState(
          window.history.state,
          "",
          window.location.pathname + "?" + params.toString()
        );
      }

      return { orderId, guestToken: orderToken };
    } catch (error) {
      console.error("Error creating order:", error);
      throw error;
    }
  };

  return (
    <OrderContext.Provider
      value={{
        mainOrderId,
        guestToken,
        activeOrderFinalized,
        restoreWasReconciled,
        rescuedGroupIdsSnapshot,
        dismissRestoreReconciled: () => {
          setRestoreWasReconciled(false);
          setRescuedGroupIdsSnapshot([]);
        },
        setOrderInfo,
        ensureOrderExists,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};

export const useOrder = (): OrderContextType => {
  const context = useContext(OrderContext);
  if (context === undefined) {
    throw new Error("useOrder must be used within an OrderProvider");
  }
  return context;
};

// Define the context type for Coupons
interface CouponContextType {
  coupon: GetCouponDto | null;
  setCoupon: React.Dispatch<React.SetStateAction<GetCouponDto | null>>;
}

// Create the context with an undefined initial value
const CouponContext = createContext<CouponContextType | undefined>(undefined);

// Define the provider props interface
interface CouponProviderProps {
  children: ReactNode;
}

// Create the CouponsProvider component
export const CouponProvider: React.FC<CouponProviderProps> = ({ children }) => {
  const [coupon, setCoupon] = useState<GetCouponDto | null>(null);

  return (
    <CouponContext.Provider value={{ coupon, setCoupon }}>
      {children}
    </CouponContext.Provider>
  );
};

// Create a custom hook to use the CouponsContext
export const useCoupon = (): CouponContextType => {
  const context = useContext(CouponContext);
  if (context === undefined) {
    throw new Error("useCoupon must be used within a CouponProvider");
  }
  return context;
};
